import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { errorHandler } from '../../utils/errorHandler';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getCountryName } from '../../utils/locationUtils';
import EventPartners from '../../components/features/Events/EventPartners';
import './EventPage.css';

function EventPage() {
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { eventId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    useEffect(() => {
        if (!eventId) {
            setError('Event ID is missing or invalid.');
            setLoading(false);
            return;
        }
        fetchEvent();
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            setError(null);
            if (!eventId) {
                throw new Error('Event ID is missing or invalid');
            }
            const eventResponse = await eventsAPI.getEvent(eventId);
            if (eventResponse) {
                setEvent(eventResponse);
            } else {
                throw new Error('No event data received');
            }
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setEvent(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="event-page__error-container">
                <p className="event-page__error-message">{error}</p>
                <button onClick={() => navigate('/activities')} className="event-page__back-button">
                    Back to Activities
                </button>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="event-page__error-container">
                <p className="event-page__error-message">Event not found.</p>
                <button onClick={() => navigate('/activities')} className="event-page__back-button">
                    Back to Activities
                </button>
            </div>
        );
    }

    return (
        <div className="event-page">
            <div className="event-page__hero">
                <div 
                    className="event-page__hero-image" 
                    style={{ backgroundImage: `url(${event.imageUrl || '/default-event.jpg'})` }}
                >
                    <div className="event-page__hero-overlay">
                        <h1>{event.title}</h1>
                        <div className="event-page__metadata">
                            <div className="event-page__metadata-item">
                                <i className="fas fa-map-marker-alt"></i>
                                <span>
                                    {event.city}
                                    {event.country && `, ${getCountryName(event.country)}`}
                                </span>
                            </div>
                            <div className="event-page__metadata-item">
                                <i className="fas fa-calendar-alt"></i>
                                <span>{new Date(event.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}</span>
                            </div>
                            <div className="event-page__metadata-item">
                                <i className="fas fa-tag"></i>
                                <span>{event.type}</span>
                            </div>
                            <div className="event-page__metadata-item">
                                <i className="fas fa-info-circle"></i>
                                <span>{event.status}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="event-page__content">
                <div className="event-page__description">
                    <h2>About this Event</h2>
                    <p>{event.description}</p>
                </div>

                {event.requirements && (
                    <div className="event-page__requirements">
                        <h2>Requirements</h2>
                        <ul className="event-page__requirements-list">
                            {typeof event.requirements === 'string' 
                                ? event.requirements.split('\n')
                                    .filter(req => req.trim())
                                    .map((requirement, index) => (
                                        <li key={index}>
                                            <i className="fas fa-check-circle"></i>
                                            {requirement.trim()}
                                        </li>
                                    ))
                                : null
                            }
                        </ul>
                    </div>
                )}

                {event.partners && event.partners.length > 0 && (
                    <div className="event-page__partners">
                        <h2>Event Partners</h2>
                        <EventPartners partnerIds={event.partners} />
                    </div>
                )}

                {event.registrationLink && (
                    <div className="event-page__registration">
                        <a 
                            href={event.registrationLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="event-page__registration-button"
                        >
                            Register for this Event
                        </a>
                    </div>
                )}

                <div className="event-page__back-container">
                    <button 
                        className="event-page__back-button"
                        onClick={() => navigate('/activities')}
                    >
                        <i className="fas fa-arrow-left"></i> Back to Activities
                    </button>
                </div>
            </div>
        </div>
    );
}

export default EventPage; 